import AnimatingDots from '~/components/AnimatingDots';
import Divider from '~/components/Divider';
import Gap from '~/components/Gap';
import Text from '~/components/Text';
import { useIsDesktop } from '~/hooks/useIsDesktop';
import { fmPrice } from '~/utils/amount';
import { formatDate } from '~/utils/datetime';
import InvoiceContextMenu from '../CellContent/InvoiceContextMenu';
import InvoiceStatusPills from '../CellContent/InvoiceStatusPills';
import InvoiceDueStatus from '../InvoiceDueStatus';
import { BILLING_ROUTE } from '~/constants/routes';
import InvoiceLink from './InvoiceLink';
import { useInvoiceDetails } from './useInvoiceDetails';
import * as Styles from './styles';
import { useSelector } from '~/store';
import { RootState } from '~/types';
import StyledAnchor from '~/components/StyledAnchor';
import StyledLink from '~/components/StyledLink';
import ButtonLink from '~/components/ButtonLink';

interface IInvoiceDetailsProps {
  invoiceUuid: string | null;
}

const InvoiceDetails = ({ invoiceUuid }: IInvoiceDetailsProps) => {
  const { isLoading, invoiceData } = useInvoiceDetails(invoiceUuid);
  const isDesktop = useIsDesktop();
  const { currentPlan } = useSelector((state: RootState) => state.user);

  if (isLoading || invoiceUuid === null)
    return (
      <Styles.Wrapper className="full-width center">
        <Styles.Section>
          <AnimatingDots />
        </Styles.Section>
      </Styles.Wrapper>
    );

  return (
    <Styles.Wrapper className="full-width">
      <Styles.Section>
        {isDesktop ? (
          <div className="spread align-baseline">
            <div>
              <Text size="xs" variant="neutral" className="semi-bold">
                Invoice total
              </Text>
              <Gap t={8} />
              <Text size="slg" variant="themed" className="bold">
                {fmPrice(invoiceData.currency, invoiceData.total_amount)}
              </Text>
            </div>
            <div>
              <div className="center">
                <InvoiceStatusPills invoiceData={invoiceData} />
                <Gap l={8} />
                <InvoiceContextMenu invoiceReferenceId={invoiceUuid} />
              </div>
              <Gap t={4} />
              <InvoiceDueStatus invoiceData={invoiceData} />
            </div>
          </div>
        ) : (
          <div className="align-center">
            <div className="full-flex">
              <div className="v-flex-start">
                <div>
                  <Text size="xs" variant="neutral" className="semi-bold">
                    Invoice total
                  </Text>
                  <Gap t={8} />
                  <Text size="md" variant="themed" className="bold">
                    {fmPrice(invoiceData.currency, invoiceData.total_amount)}
                  </Text>
                  <Gap t={6} />
                </div>
                <div>
                  <InvoiceStatusPills invoiceData={invoiceData} />
                  <Gap t={4} />
                  <InvoiceDueStatus invoiceData={invoiceData} />
                </div>
              </div>
            </div>
            <InvoiceContextMenu invoiceReferenceId={invoiceUuid} />
          </div>
        )}

        <InvoiceLink invoiceUuid={invoiceData.invoice_uuid} pdfUrl={invoiceData.pdf_url} />
      </Styles.Section>

      <Divider />

      <Styles.Section>
        <div className="d-flex">
          <div className="full-flex">
            <Text size="xs" variant="neutral" className="semi-bold">
              Invoice number
            </Text>
            <Gap t={4} />
            <Text size="sm" className="semi-bold">
              {invoiceData.invoice_number}
            </Text>
            <Gap t={20} />
            <Text size="xs" variant="neutral" className="semi-bold">
              Purchase order number
            </Text>
            <Gap t={4} />
            <Text size="sm" className="semi-bold">
              {invoiceData.po_number}
            </Text>
          </div>
          <div className="full-flex">
            <Text size="xs" variant="neutral">
              Invoice date
            </Text>
            <Gap t={4} />
            <Text size="sm" className="semi-bold">
              {formatDate(invoiceData.invoice_date)}
            </Text>
            <Gap t={20} />
            <Text size="xs" variant="neutral" className="semi-bold">
              Invoice due date
            </Text>
            <Gap t={4} />
            <Text size="sm" className="semi-bold">
              {formatDate(invoiceData.due_date)}
            </Text>
          </div>
        </div>
      </Styles.Section>

      <Divider />

      <Styles.Section>
        <Text size="sm" className="bold">
          Client Details
        </Text>
        <Gap t={20} />
        <Text size="sm" className="semi-bold">
          {invoiceData.buyer_company_name}
        </Text>
        <Gap t={4} />
        <Text size="xs" variant="secondary">
          {invoiceData.buyer_address_line_1}
        </Text>
        <Text size="xs" variant="secondary">
          {invoiceData.buyer_address_line_2}
        </Text>
        <Text size="xs" variant="secondary">
          {invoiceData.buyer_city}
        </Text>
        <Text size="xs" variant="secondary">
          {invoiceData.buyer_state}
        </Text>
        <Text size="xs" variant="secondary">
          {invoiceData.buyer_country}
        </Text>
        {invoiceData.buyer_email && (
          <Text size="xs" variant="secondary">
            Email: {invoiceData.buyer_email}
          </Text>
        )}
      </Styles.Section>

      <Divider />

      <Styles.Section>
        <Text size="sm" className="bold">
          Line items
        </Text>
        <Gap t={20} />

        <Styles.ItemList>
          {invoiceData.items.map(eachItem => {
            return (
              <Styles.ItemListItem key={eachItem.id} className="spread align-baseline">
                <div>
                  <Text size="sm" className="semi-bold">
                    {eachItem.description}
                  </Text>
                  <Gap t={2} />
                  <Text size="xs" className="semi-bold" variant="secondary">
                    Qty: {eachItem.quantity}
                  </Text>
                  <Gap t={2} />
                  <Text size="xs" className="semi-bold" variant="secondary">
                    Unit Price: {fmPrice(invoiceData.currency, eachItem.price)}
                  </Text>
                </div>

                <div>
                  <Text size="sm" className="bold">
                    {fmPrice(invoiceData.currency, eachItem.quantity * eachItem.price)}
                  </Text>
                </div>
              </Styles.ItemListItem>
            );
          })}
        </Styles.ItemList>
      </Styles.Section>

      <Divider />
      <Styles.Section>
        <div className="full-width spread">
          <Text size="sm" className="semi-bold">
            Sub Total
          </Text>
          <Text size="sm" className="semi-bold">
            {fmPrice(invoiceData.currency, invoiceData.sub_total)}
          </Text>
        </div>
        <Gap t={14} />
        {invoiceData.discount_amount ? (
          <>
            <div className="full-width spread">
              <Text variant="secondary" size="sm" className="semi-bold">
                Discount
                {invoiceData.discount_percentage ? ` (${invoiceData.discount_percentage}%)` : ''}
              </Text>
              <Text variant="secondary" size="sm" className="semi-bold">
                -{fmPrice(invoiceData.currency, invoiceData.discount_amount)}
              </Text>
            </div>
            <Gap t={14} />
          </>
        ) : null}

        <div className="full-width spread">
          <Text variant="secondary" size="sm" className="semi-bold">
            Tax
            {invoiceData.tax_percentage ? ` (${invoiceData.tax_percentage}%)` : ''}
          </Text>
          <Text variant="secondary" size="sm" className="semi-bold">
            {fmPrice(invoiceData.currency, invoiceData.tax_amount)}
          </Text>
        </div>
        <Gap t={14} />
        <div className="full-width spread">
          <Text variant="secondary" size="sm" className="semi-bold">
            Shipping
            {invoiceData.shipping_percentage ? ` (${invoiceData.shipping_percentage}%)` : ''}
          </Text>
          <Text variant="secondary" size="sm" className="semi-bold">
            {fmPrice(invoiceData.currency, invoiceData.shipping_amount)}
          </Text>
        </div>
        <Gap t={14} />
      </Styles.Section>

      <Divider />
      <Styles.Section>
        <div className="full-width spread">
          <Text size="sm" className="bold">
            Total Amount
          </Text>
          <Text size="sm" className="bold">
            {fmPrice(invoiceData.currency, invoiceData.total_amount)}
          </Text>
        </div>
      </Styles.Section>

      {invoiceData.pdf_url && (
        <>
          <Divider />
          <Styles.Section>
            <Text size="sm" className="bold">
              Invoice Preview
            </Text>
            <Gap t={20} />
            <Styles.PdfPreview src={invoiceData.pdf_url} title="Invoice PDF Preview" />
            <Gap t={16} />
            {currentPlan === 'free' && (
              <ButtonLink size="sm" variant="danger" to={BILLING_ROUTE} className="full-width">
                Remove Powered By createmyinvoice.com
              </ButtonLink>
            )}
          </Styles.Section>
        </>
      )}
      <Gap t={70} />
    </Styles.Wrapper>
  );
};

export default InvoiceDetails;
